import React, { useMemo, useRef, useState } from 'react';
import classes from './SelectAutoComplete.module.scss';
import { UseFormReturn } from 'react-hook-form';
import {
  MultiSelectProps,
  SelectDropdownTemplate,
  SelectDropdownTemplateProps,
  SelectItem
} from '../Select/Select.view';
import { FormElementValueType } from '../../../models/basic-objects';
import { Input } from '../Input';
import { Loader } from '../../containers/Loader';
import { SquareButtonIconsEnum } from '../../../constants/square-button-icons';
import { idGenerator } from '../../../helpers/idGenerator';
import { OutsideClickWrapper } from '../../actions/OutsideClickWrapper';
import { DropdownWrapper } from '../../containers/DropdownWrapper';

export type SelectAutoCompleteViewProps = {
  name: string;
  items: SelectItem[];
  formInstance: UseFormReturn;
  size: 'small' | 'medium' | 'large';
  label?: string;
  placeholder?: string;
  callBackHandler: CallableFunction;
  resetCallBackHandler?: CallableFunction;
  disabled?: boolean;
  loading?: boolean;
  selectedValue?: FormElementValueType;
  dropdownTemplate: SelectDropdownTemplate;
  side?: 'right' | 'left' | undefined;
};

const multipleObject: MultiSelectProps = { multiple: false, visibleMultipleValues: undefined as never };

export const SelectAutoCompleteView = (props: SelectAutoCompleteViewProps): JSX.Element => {
  const {
    name,
    items,
    loading,
    formInstance,
    size,
    label,
    placeholder,
    dropdownTemplate,
    callBackHandler,
    resetCallBackHandler,
    disabled,
    selectedValue,
    side
  } = props;

  const inputID = useMemo(() => idGenerator(name), [name]);
  const inputWatchValue = formInstance.watch(name);
  const dropDownRef = useRef<HTMLDivElement>(null);
  // If we have a selectedValue on first mount, set open to false
  const [open, setOpen] = useState<boolean>(false);

  // We return an empty value template because we don't use this part of the select, we use
  // a separate <Input /> element

  const selectProps: SelectDropdownTemplateProps = useMemo(
    () => ({
      chooseLabel: '',
      disabled: disabled || loading,
      dropdownContainerHeightType: 'desktop',
      expanded: open,
      hasOptionLabels: false,
      intentType: 'mouse',
      items,
      label: undefined,
      name: `${name}Select`,
      optGroupTuples: [],
      rules: {},
      selectedLabel: '',
      selectedValue,
      setExpanded: setOpen,
      size,
      updateValue: (_value: FormElementValueType) => {
        /****/
      },
      useFormInstance: formInstance,
      ...multipleObject
    }),
    [items, name, size, disabled, loading, formInstance, selectedValue, open]
  );

  // if we are loading, we do not show reset button
  // if we have no value, we do not show reset button
  const resetInputProps = useMemo(() => {
    if (inputWatchValue && !loading) {
      return {
        actionButton: { icon: SquareButtonIconsEnum.closeSmall, disabled: !inputWatchValue },
        action: () => {
          formInstance.setValue(name, '');

          if (resetCallBackHandler) {
            resetCallBackHandler();
          }
        }
      };
    }

    return {};
  }, [formInstance, inputWatchValue, loading, name, resetCallBackHandler]);

  const onClickOutsideHandler = () => setOpen(false);

  return (
    <OutsideClickWrapper
      onClickOutside={onClickOutsideHandler}
      onFocusOutside={onClickOutsideHandler}
      ref={dropDownRef}
      className={classes.SelectAutoComplete}>
      {/* This timeout workaround is to let autocomplete render completely before allowing subsequent components to
      render, fixing a bug where outsideclickwrapper no longer functions and breaking autocomplete */}
      <div onFocus={() => setTimeout(() => setOpen(true), 0)}>
        <Input
          {...resetInputProps}
          label={label}
          placeholder={placeholder}
          formInstance={formInstance}
          id={inputID}
          name={name}
          rules={{}}
          size={size}
          disabled={disabled}
          updateAction={(value, complete) => {
            complete(true);
            callBackHandler(value);
          }}
          onKeyDown={e => {
            // Prevent Input from processing ENTER key presses
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          updateDebounceTiming={500}
        />
      </div>
      <DropdownWrapper id={`portal-dropdown-${inputID}`} expanded={open} portalWrapperRef={dropDownRef} side={side}>
        {dropdownTemplate(selectProps, '')}
      </DropdownWrapper>
      <div className={classes.InputStatusWrapper}>
        {loading && (
          <div className={classes.LoaderWrapper}>
            <Loader variation={'dark'} size={'small'} type={'inline'} />
          </div>
        )}
      </div>
    </OutsideClickWrapper>
  );
};
